import {
  Api,
  BugReport,
  Clear,
  Close,
  ContentCopy,
  DeleteForever,
  ExpandLess,
  ExpandMore,
  OpenInNew,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  IconButton,
  Link,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useDebug, type DebugLogEntry } from '../../contexts/DebugContext';
import { useEntraAuth } from '../../contexts/EntraAuthContext';
import { clearTrainingPlan } from '../../services/trainingPlanStorage';

/**
 * Debug Overlay Component
 * Shows a floating panel with scrollable logs when debug mode is enabled
 */

const LogLevelChip: React.FC<{ level: DebugLogEntry['level'] }> = ({ level }) => {
  const getColor = () => {
    switch (level) {
      case 'error':
        return 'error';
      case 'warn':
        return 'warning';
      case 'info':
        return 'info';
      case 'debug':
      default:
        return 'default';
    }
  };

  return (
    <Chip
      label={level.toUpperCase()}
      color={getColor()}
      size="small"
      sx={{ minWidth: 60, fontSize: '0.65rem', height: 18 }}
    />
  );
};

const formatTime = (timestamp: string): string => {
  try {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return timestamp;
  }
};

const formatMeta = (meta?: object): string => {
  if (!meta || Object.keys(meta).length === 0) return '';
  try {
    return JSON.stringify(meta, null, 0);
  } catch {
    return String(meta);
  }
};

export const DebugOverlay: React.FC = () => {
  const theme = useTheme();
  const { isDebugMode, logs, clearLogs } = useDebug();
  const { getAccessToken, isAuthenticated } = useEntraAuth();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Severity filter state - which levels are visible
  const [visibleLevels, setVisibleLevels] = useState<Set<DebugLogEntry['level']>>(
    new Set(['debug', 'info', 'warn', 'error'])
  );

  // API Modal state
  const [apiModalOpen, setApiModalOpen] = useState(false);
  const [apiModalTitle, setApiModalTitle] = useState('');
  const [apiModalData, setApiModalData] = useState<string>('');
  const [apiModalLoading, setApiModalLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Delete plan confirmation dialog state
  const [deletePlanDialogOpen, setDeletePlanDialogOpen] = useState(false);

  // Get API base URL
  const apiBase = import.meta.env.VITE_API_BASE || 'http://localhost:3000';

  // Fetch API data and show in modal
  const fetchAndShowApi = async (url: string, title: string, requiresAuth: boolean = false) => {
    setApiModalTitle(title);
    setApiModalOpen(true);
    setApiModalLoading(true);
    setApiModalData('');
    setCopySuccess(false);

    try {
      const fullUrl = `${apiBase}${url}`;
      const headers: Record<string, string> = {};

      if (requiresAuth) {
        const token = await getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      const response = await fetch(fullUrl, { headers });
      const data = await response.json();
      setApiModalData(JSON.stringify(data, null, 2));
    } catch (error) {
      setApiModalData(
        `Error fetching data: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setApiModalLoading(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(apiModalData);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // Handle delete training plan
  const handleDeleteTrainingPlan = () => {
    clearTrainingPlan();
    setDeletePlanDialogOpen(false);
    // Reload the page to reset app state
    window.location.reload();
  };

  // Toggle a severity level
  const toggleLevel = (level: DebugLogEntry['level']) => {
    setVisibleLevels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(level)) {
        // Don't allow hiding all levels
        if (newSet.size > 1) {
          newSet.delete(level);
        }
      } else {
        newSet.add(level);
      }
      return newSet;
    });
  };

  // Quick filter presets
  const setPreset = (preset: 'all' | 'errors' | 'warnings') => {
    switch (preset) {
      case 'all':
        setVisibleLevels(new Set(['debug', 'info', 'warn', 'error']));
        break;
      case 'errors':
        setVisibleLevels(new Set(['error']));
        break;
      case 'warnings':
        setVisibleLevels(new Set(['warn', 'error']));
        break;
    }
  };

  // Filter logs by visible levels
  const filteredLogs = logs.filter(log => visibleLevels.has(log.level));

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && logsEndRef.current && isExpanded && !isMinimized) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScroll, isExpanded, isMinimized]);

  if (!isDebugMode) {
    return null;
  }

  if (isMinimized) {
    return (
      <Fab
        color="primary"
        size="small"
        onClick={() => setIsMinimized(false)}
        sx={{
          position: 'fixed',
          bottom: 80,
          right: 16,
          zIndex: 9999,
        }}
        aria-label="Open debug panel"
      >
        <BugReport />
      </Fab>
    );
  }

  const errorCount = logs.filter(l => l.level === 'error').length;
  const warnCount = logs.filter(l => l.level === 'warn').length;
  const infoCount = logs.filter(l => l.level === 'info').length;
  const debugCount = logs.filter(l => l.level === 'debug').length;

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 80,
        left: 8,
        right: 8,
        maxHeight: isExpanded ? '50vh' : 48,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        transition: 'max-height 0.2s ease-in-out',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.5,
          bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
          borderBottom: `1px solid ${theme.palette.divider}`,
          minHeight: 40,
          flexWrap: 'wrap',
          gap: 0.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BugReport fontSize="small" color="primary" />
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            sx={{ display: { xs: isExpanded ? 'block' : 'none', sm: 'block' } }}
          >
            Debug
          </Typography>
          <Chip
            label={`${filteredLogs.length}/${logs.length}`}
            size="small"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        </Box>

        {/* Severity Filter Chips - only show when expanded */}
        {isExpanded && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Toggle errors">
              <Chip
                label={`E:${errorCount}`}
                color={visibleLevels.has('error') ? 'error' : 'default'}
                variant={visibleLevels.has('error') ? 'filled' : 'outlined'}
                size="small"
                onClick={() => toggleLevel('error')}
                sx={{
                  height: 22,
                  fontSize: '0.65rem',
                  cursor: 'pointer',
                  opacity: visibleLevels.has('error') ? 1 : 0.5,
                }}
              />
            </Tooltip>
            <Tooltip title="Toggle warnings">
              <Chip
                label={`W:${warnCount}`}
                color={visibleLevels.has('warn') ? 'warning' : 'default'}
                variant={visibleLevels.has('warn') ? 'filled' : 'outlined'}
                size="small"
                onClick={() => toggleLevel('warn')}
                sx={{
                  height: 22,
                  fontSize: '0.65rem',
                  cursor: 'pointer',
                  opacity: visibleLevels.has('warn') ? 1 : 0.5,
                }}
              />
            </Tooltip>
            <Tooltip title="Toggle info">
              <Chip
                label={`I:${infoCount}`}
                color={visibleLevels.has('info') ? 'info' : 'default'}
                variant={visibleLevels.has('info') ? 'filled' : 'outlined'}
                size="small"
                onClick={() => toggleLevel('info')}
                sx={{
                  height: 22,
                  fontSize: '0.65rem',
                  cursor: 'pointer',
                  opacity: visibleLevels.has('info') ? 1 : 0.5,
                }}
              />
            </Tooltip>
            <Tooltip title="Toggle debug">
              <Chip
                label={`D:${debugCount}`}
                color={visibleLevels.has('debug') ? 'default' : 'default'}
                variant={visibleLevels.has('debug') ? 'filled' : 'outlined'}
                size="small"
                onClick={() => toggleLevel('debug')}
                sx={{
                  height: 22,
                  fontSize: '0.65rem',
                  cursor: 'pointer',
                  opacity: visibleLevels.has('debug') ? 1 : 0.5,
                }}
              />
            </Tooltip>
            <Box sx={{ width: 1, height: 16, bgcolor: 'divider', mx: 0.5 }} />
            <Tooltip title="Show all levels">
              <Chip
                label="All"
                size="small"
                onClick={() => setPreset('all')}
                sx={{ height: 20, fontSize: '0.6rem', cursor: 'pointer' }}
              />
            </Tooltip>
            <Tooltip title="Show errors only">
              <Chip
                label="Err"
                color="error"
                variant="outlined"
                size="small"
                onClick={() => setPreset('errors')}
                sx={{ height: 20, fontSize: '0.6rem', cursor: 'pointer' }}
              />
            </Tooltip>
            <Box sx={{ width: 1, height: 16, bgcolor: 'divider', mx: 0.5 }} />
            {/* API Buttons */}
            <Tooltip title={isAuthenticated ? 'View AI Coach Profile JSON' : 'Login required'}>
              <Chip
                icon={<Api sx={{ fontSize: 14 }} />}
                label="AI Profile"
                size="small"
                onClick={() => fetchAndShowApi('/api/profile/ai-coach', 'AI Coach Profile', true)}
                disabled={!isAuthenticated}
                sx={{
                  height: 22,
                  fontSize: '0.65rem',
                  cursor: isAuthenticated ? 'pointer' : 'not-allowed',
                }}
                color="primary"
                variant="outlined"
              />
            </Tooltip>
            <Tooltip title="Open Swagger API Docs">
              <Link
                href="/api-docs"
                target="_blank"
                rel="noopener"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.65rem', ml: 1 }}
              >
                <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                  Swagger
                </Typography>
                <OpenInNew sx={{ fontSize: 10 }} />
              </Link>
            </Tooltip>
            <Box sx={{ width: 1, height: 16, bgcolor: 'divider', mx: 0.5 }} />
            {/* Debug Actions */}
            <Tooltip title="Delete training plan from local storage">
              <Chip
                icon={<DeleteForever sx={{ fontSize: 14 }} />}
                label="Delete Plan"
                size="small"
                onClick={() => setDeletePlanDialogOpen(true)}
                sx={{
                  height: 22,
                  fontSize: '0.65rem',
                  cursor: 'pointer',
                }}
                color="error"
                variant="outlined"
                aria-label="Delete training plan from local storage"
              />
            </Tooltip>
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Clear logs">
            <IconButton size="small" onClick={clearLogs} aria-label="Clear logs">
              <Clear fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
            <IconButton
              size="small"
              onClick={() => setIsExpanded(!isExpanded)}
              aria-label={isExpanded ? 'Collapse logs' : 'Expand logs'}
            >
              {isExpanded ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Minimize">
            <IconButton
              size="small"
              onClick={() => setIsMinimized(true)}
              aria-label="Minimize debug panel"
            >
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Logs container */}
      {isExpanded && (
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            bgcolor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#fafafa',
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            p: 1,
          }}
          onScroll={e => {
            const target = e.target as HTMLDivElement;
            const isAtBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 50;
            setAutoScroll(isAtBottom);
          }}
        >
          {filteredLogs.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center', py: 2, fontStyle: 'italic' }}
            >
              {logs.length === 0
                ? 'No logs yet. Interact with the app to see logs.'
                : 'No logs match the current filter.'}
            </Typography>
          ) : (
            filteredLogs.map(log => (
              <Box
                key={log.id}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'flex-start' },
                  gap: { xs: 0.25, sm: 1 },
                  py: 0.5,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  '&:last-child': { borderBottom: 'none' },
                  color:
                    log.level === 'error'
                      ? theme.palette.error.main
                      : log.level === 'warn'
                        ? theme.palette.warning.main
                        : theme.palette.text.primary,
                }}
              >
                {/* Header row: timestamp + level */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: '0.7rem',
                      minWidth: { xs: 'auto', sm: 70 },
                    }}
                  >
                    {formatTime(log.timestamp)}
                  </Typography>
                  <LogLevelChip level={log.level} />
                </Box>
                {/* Message */}
                <Box sx={{ flex: 1, wordBreak: 'break-word' }}>
                  <Typography component="span" sx={{ fontSize: '0.75rem' }}>
                    {log.message}
                  </Typography>
                  {log.meta && Object.keys(log.meta).length > 0 && (
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '0.7rem',
                        color: theme.palette.text.secondary,
                        ml: 1,
                        wordBreak: 'break-all',
                      }}
                    >
                      {formatMeta(log.meta)}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))
          )}
          <div ref={logsEndRef} />
        </Box>
      )}

      {/* API JSON Modal */}
      <Dialog
        open={apiModalOpen}
        onClose={() => setApiModalOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{
          zIndex: 10000, // Ensure modal is on top of debug overlay (z-index: 9999)
        }}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Api color="primary" />
            <Typography variant="h6">{apiModalTitle}</Typography>
          </Box>
          <IconButton onClick={() => setApiModalOpen(false)} size="small" aria-label="Close modal">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {apiModalLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              component="pre"
              sx={{
                bgcolor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5',
                p: 2,
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: '60vh',
                fontSize: '0.8rem',
                fontFamily: 'monospace',
                m: 0,
              }}
            >
              {apiModalData}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={copySuccess ? null : <ContentCopy />}
            onClick={copyToClipboard}
            disabled={apiModalLoading || !apiModalData}
            color={copySuccess ? 'success' : 'primary'}
            variant="contained"
          >
            {copySuccess ? '✓ Copied!' : 'Copy to Clipboard'}
          </Button>
          <Button onClick={() => setApiModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Training Plan Confirmation Dialog */}
      <Dialog
        open={deletePlanDialogOpen}
        onClose={() => setDeletePlanDialogOpen(false)}
        sx={{
          zIndex: 10000,
        }}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteForever color="error" />
          <Typography variant="h6">Delete Training Plan</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the training plan from local storage? This will clear
            the cached plan and reload the page.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePlanDialogOpen(false)} aria-label="Cancel delete">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteTrainingPlan}
            color="error"
            variant="contained"
            startIcon={<DeleteForever />}
            aria-label="Confirm delete training plan"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default DebugOverlay;
